/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container,
    Typography,
    Grid
} from '@mui/material';
import Page from '../components/Page';

import { RankingService } from '../application/RankingService';
import StatCard from '../components/Emissions/StatCard';
import DataTable from '../components/Emissions/DataTable';
import OperationEfficiencyChart from '../components/Emissions/OperationEfficiencyChart';
import EmissionMapDialog from '../components/Emissions/EmissionMapDialog';


const rankingService = new RankingService();

export default function Dashboard() {
    const Region = localStorage.getItem('region');
    const [stats, setStats] = useState({
        totalTrips: 0,
        totalDistance: 0,
        totalEmissions: 0,
        avgEmissionsPerPassenger: 0,
        estimatedCarbonFee: 0,
        emission_per_km: 0,
        emission_reduction_percentage: 0
    });

    const [chartData, setChartData] = useState([]);
    const [mileageRanking, setMileageRanking] = useState([]);
    const [emissionsRanking, setEmissionsRanking] = useState([]);
    const [reductionPercentage, setReductionPercentage] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [emissionTrail, setEmissionTrail] = useState([]);
    const [openMap, setOpenMap] = useState(false);

    useEffect(() => {

        fetchRankings();
        fetchStats();
    }, []);



    const fetchRankings = useCallback(async () => {
        try {
            // 使用新的 API 
            const rankings = await rankingService.getNewRankings(Region);

            // 更新各項排名數據
            setMileageRanking(rankings.mileageRanking.map(item => ({
                vehicleId: item.vehicleId,
                value: `${item.mileage} km`
            })));

            setEmissionsRanking(rankings.emissionsRanking.map(item => ({
                vehicleId: item.vehicleId,
                value: `${item.emissions} g CO2`

            })));

            setReductionPercentage(rankings.reductionPercentage.map(item => ({
                vehicleId: item.vehicleId,
                current: `${item.current} %`,
                previous: `${item.previous} %`
            })));
        } catch (error) {
            console.error('Error fetching rankings:', error);
            // 保持原有的錯誤處理邏輯
        }
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            const statsData = await rankingService.getMonthlyStats(Region);
            // 近六月碳排放數據
            const emissionsData = await rankingService.getLastSixMonthsEmissions(Region);
            setStats(prevStats => ({
                ...prevStats,
                totalDistance: statsData.totalMileage,
                totalEmissions: statsData.totalEmission,
                emission_per_km: statsData.emission_per_km,
                emission_reduction_percentage: statsData.emission_reduction_percentage,
            }));
            const chartData = emissionsData.map(item => ({
                year: item.year,
                month: item.month,
                totalMileage: item.totalMileage,
                totalFuelConsumed: item.totalFuelConsumed,
                totalFuelExpense: item.totalFuelExpense,
                totalCarbonEmission: item.totalCarbonEmission
            }));
            setChartData(chartData);

        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, []);
    const handleVehicleClick = useCallback((vehicleId) => {
        setSelectedVehicle(vehicleId);
        setOpenMap(true); // 先打開地圖對話框
    }, []);

    const handleCloseMap = () => {
        setOpenMap(false);
    };

    const memoizedChartData = useMemo(() => chartData, [chartData]);
    const memoizedMileageRanking = useMemo(() => mileageRanking, [mileageRanking]);
    const memoizedEmissionsRanking = useMemo(() => emissionsRanking, [emissionsRanking]);
    const memoizedReductionPercentage = useMemo(() => reductionPercentage, [reductionPercentage]);

    return (
        <Page title="碳排放管理 | 智慧城市移動資訊平台">
            <Container maxWidth="xl" sx={{ p: 2, borderRadius: '10px' }}>
                <Typography variant="h5" sx={{ mb: 3, color: '#2e7d32' }}>
                    碳排放資訊
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={2} md={2}>
                        <StatCard
                            title="總里程"
                            value={stats.totalDistance.toFixed(1)}
                            unit="km"
                            color="#FFB74D"
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                        <StatCard
                            title="總碳排放量"
                            value={stats.totalEmissions.toFixed(2)}
                            unit="g CO2"
                            color="#81C784"
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                        <StatCard
                            title="每公里二氧化碳排放"
                            value={stats.emission_per_km.toFixed(2)}
                            unit="g CO2/km"
                            color="#42A5F5"
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                        <StatCard
                            title="月減碳百分比"
                            value={stats.emission_reduction_percentage.toFixed(2)}
                            unit="%"
                            color="#BA68C8"
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                        <StatCard
                            title="碳排係數"
                            value="13.5"
                            unit="g CO2/km"
                            color="#F06292"
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                        <StatCard
                            title="燃油效率"
                            value="15.5"
                            unit="km/l"
                            color="#66BB6A"
                        />
                    </Grid>
                </Grid>
                <OperationEfficiencyChart data={memoizedChartData} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <DataTable
                            data={memoizedMileageRanking}
                            title="車輛里程排行"
                            bgColor="#fce4ec"
                            headers={['車號', '里程']}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DataTable
                            data={memoizedEmissionsRanking}
                            title="車輛碳排排行"
                            bgColor="#fff3e0"
                            headers={['車號', '碳排放量']}
                            onRowClick={handleVehicleClick}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DataTable
                            data={memoizedReductionPercentage}
                            title="減碳佔比%"
                            bgColor="#e8eaf6"
                            headers={['車號', '本月', '上月']}
                        />
                    </Grid>
                </Grid>
            </Container>
            <EmissionMapDialog
                openMap={openMap}
                handleCloseMap={handleCloseMap}
                emissionTrail={emissionTrail}
                selectedVehicle={selectedVehicle}
            />

        </Page>
    );
}